// client/src/coms/views/canvas_parts/party/PartyStage.js
// The in-game screen for every party mode: phase header and server-synced timer, players with scores,
// the viewer's current task (write, draw, guess, choose, vote, watch), reveals, the feed and host controls.
// What to show comes entirely from the server view; this component never decides game rules.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import UserAvatar from '../../games_parts/UserAvatar';
import PartyCanvasSurface from './PartyCanvasSurface';
import PartyDrawingView from './PartyDrawingView';
import {
  BluffReveal, ChallengeResults, ComboResults, FlipbookPlayer, RelayReveal, RoundResults, TeamReveal, TurnReveal, VoteGallery, nameOf,
} from './PartyReveals';

const NO_PLAYERS = [];
const WRITE_ACTION = { 'sketch-relay': 'submit', 'flipbook-frenzy': 'title', 'bluff-brush': 'fake' };

export function useServerClock(serverNow, active = true) {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => setNow(serverNow()), 250);
    return () => window.clearInterval(timer);
  }, [active, serverNow]);
  return now;
}

export function PhaseTimer({ phase, serverNow }) {
  const now = useServerClock(serverNow, Boolean(phase && phase.endsAt));
  if (!phase || !phase.endsAt) return phase ? <div className="party-timer untimed" title="No timer">∞</div> : null;
  const total = Math.max(1, phase.endsAt - phase.startedAt);
  const left = Math.max(0, phase.endsAt - now);
  const fraction = left / total;
  const seconds = Math.ceil(left / 1000);
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className={`party-timer${seconds <= 5 ? ' urgent' : ''}`} role="timer" aria-label={`${seconds} seconds left`} data-testid="party-timer">
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <circle cx="24" cy="24" r={radius} className="track" />
        <circle cx="24" cy="24" r={radius} className="bar" style={{ strokeDasharray: circumference, strokeDashoffset: circumference * (1 - fraction) }} />
      </svg>
      <span>{seconds}</span>
    </div>
  );
}

function PlayerStrip({ view, game, highlight }) {
  const players = game.players || NO_PLAYERS;
  const members = new Map(view.members.map((member) => [member.userId, member]));
  const previous = useRef(new Map());
  const [deltas, setDeltas] = useState({});
  useEffect(() => {
    const changes = {};
    players.forEach((player) => {
      const before = previous.current.get(player.userId);
      if (before !== undefined && player.score > before) changes[player.userId] = player.score - before;
      previous.current.set(player.userId, player.score);
    });
    if (Object.keys(changes).length) {
      setDeltas(changes);
      const timer = window.setTimeout(() => setDeltas({}), 1800);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [players]);
  const teams = game.teams ? new Map(game.teams.map((team) => [team.id, team])) : null;
  const sorted = players.slice().sort((a, b) => b.score - a.score);
  return (
    <ol className="party-players" aria-label="Players">
      {sorted.map((player) => {
        const member = members.get(player.userId);
        const status = highlight ? highlight(player.userId) : '';
        const team = teams && teams.get(player.team);
        return (
          <li key={player.userId} className={`party-player${player.gone ? ' gone' : ''}${member && !member.connected ? ' offline' : ''}${player.userId === view.me.userId ? ' me' : ''}`} style={team ? { borderColor: team.color } : undefined}>
            <span className="party-player-avatar">
              <UserAvatar userId={player.userId} name={player.name} size={30} />
              {member && !member.connected && !player.gone ? <span className="party-badge warn" title="Reconnecting">⟳</span> : null}
              {status ? <span className="party-badge" title={status}>{status === 'drawing' ? '✏️' : status === 'done' ? '✓' : status === 'guessed' ? '★' : '·'}</span> : null}
            </span>
            <span className="party-player-name">{player.name}{member && member.isHost ? <span className="party-host-tag" title="Host">👑</span> : null}</span>
            <span className="party-player-score">{player.score}</span>
            {deltas[player.userId] ? <span className="party-score-pop">+{deltas[player.userId]}</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

function Feed({ view, compact }) {
  const list = useRef(null);
  const entries = (view.feed || []).filter((entry) => entry.kind !== 'chat' || view.status !== 'playing');
  useEffect(() => {
    if (list.current) list.current.scrollTop = list.current.scrollHeight;
  }, [entries.length]);
  return (
    <div className={`party-feed${compact ? ' compact' : ''}`} ref={list} aria-live="polite" aria-label="Game feed" data-testid="party-feed">
      {entries.map((entry) => (
        <div key={entry.id} className={`party-feed-entry ${entry.kind}`}>
          {entry.kind === 'guess' ? <><strong>{entry.name}</strong> {entry.text}</> : null}
          {entry.kind === 'correct' ? <><strong>{entry.name}</strong> guessed it! {entry.points ? `+${entry.points}` : ''}</> : null}
          {entry.kind === 'chat' ? <><strong>{entry.name}:</strong> {entry.text}</> : null}
          {entry.kind === 'system' || entry.kind === 'reveal' ? entry.text : null}
        </div>
      ))}
    </div>
  );
}

function WriteTask({ task, onSubmit, drawingView }) {
  const [text, setText] = useState(task.text || '');
  const [editing, setEditing] = useState(!task.submitted);
  useEffect(() => {
    if (task.submitted) setEditing(false);
  }, [task.submitted]);
  const submit = async (event) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    const result = await onSubmit(value);
    if (result && result.ok) setEditing(false);
  };
  return (
    <div className="party-task write" data-testid="task-write">
      {drawingView}
      <form onSubmit={submit} className="party-write-form">
        <label htmlFor="party-write-input" className="party-task-label">{task.label}</label>
        {task.fallbackText ? <div className="party-fallback">“{task.fallbackText}”</div> : null}
        <div className="party-write-row">
          <input id="party-write-input" value={text} maxLength={task.maxLength || 80} onChange={(event) => setText(event.target.value)} disabled={!editing} autoComplete="off" autoFocus placeholder="Type here…" data-testid="write-input" />
          {editing ? <button type="submit" className="party-btn" disabled={!text.trim()} data-testid="write-submit">Submit</button> : <button type="button" className="party-btn ghost" onClick={() => setEditing(true)}>Edit</button>}
        </div>
        {!editing ? <div className="party-submitted">Submitted ✓ Waiting for the others…</div> : null}
      </form>
    </div>
  );
}

function GuessBox({ onGuess, disabled, maxLength = 60, placeholder = 'Type your guess…' }) {
  const [text, setText] = useState('');
  const [feedback, setFeedback] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    setText('');
    const result = await onGuess(value);
    const verdict = result && result.data && result.data.verdict;
    setFeedback(verdict === 'close' ? `“${value}” is close!` : verdict === 'correct' ? 'Correct!' : '');
  };
  return (
    <form className="party-guess-box" onSubmit={submit}>
      <input value={text} onChange={(event) => setText(event.target.value)} maxLength={maxLength} placeholder={placeholder} disabled={disabled} autoComplete="off" aria-label="Your guess" data-testid="guess-input" />
      <button type="submit" className="party-btn" disabled={disabled || !text.trim()}>Guess</button>
      {feedback ? <div className={`party-guess-feedback${feedback === 'Correct!' ? ' good' : ''}`} role="status">{feedback}</div> : null}
    </form>
  );
}

function HintLetters({ hint }) {
  if (!hint) return null;
  return (
    <div className="party-hint" aria-label={`Hint: ${hint.replace(/_/g, 'blank ')}`} data-testid="party-hint">
      {Array.from(hint).map((char, index) => <span key={index} className={char === '_' ? 'blank' : char === ' ' ? 'space' : 'shown'}>{char === '_' ? '' : char}</span>)}
      <small>{hint.replace(/[^_]/g, '').length + Array.from(hint).filter((c) => c !== '_' && c !== ' ').length} letters</small>
    </div>
  );
}

function RulesBanner({ rules }) {
  if (!rules || !rules.labels || !rules.labels.length) return null;
  return <div className="party-rules-banner">{rules.labels.map((label) => <span key={label} className="party-tag warn">{label}</span>)}</div>;
}

function DrawTask({ game, view, task, extra }) {
  const surface = useRef(null);
  const [busy, setBusy] = useState(false);
  const reference = view.game.reference;
  const canFinish = task.submitted !== undefined;
  const done = async () => {
    setBusy(true);
    if (surface.current) await surface.current.flush();
    await game.act('done');
    setBusy(false);
  };
  const timeline = task.timeline || [];
  return (
    <div className="party-task draw" data-testid="task-draw">
      <div className="party-prompt-row">
        <div className="party-prompt">
          <span className="party-task-label">{task.label}</span>
          {task.prompt ? <strong data-testid="draw-prompt">{task.prompt}</strong> : null}
          {task.part ? <span className="party-tag">{task.part}</span> : null}
        </div>
        {canFinish ? (
          task.submitted
            ? <button type="button" className="party-btn ghost" onClick={() => game.act('undone')}>Keep drawing</button>
            : <button type="button" className="party-btn" onClick={done} disabled={busy} data-testid="draw-done">{busy ? 'Saving…' : 'Done'}</button>
        ) : null}
      </div>
      <RulesBanner rules={task.rules} />
      {extra}
      <div className={`party-draw-layout${reference ? ' with-reference' : ''}`}>
        {reference ? (
          <figure className="party-reference">
            <PartyDrawingView items={reference.items} fluid label="Reference to copy" />
            <figcaption>Copy this</figcaption>
          </figure>
        ) : null}
        <PartyCanvasSurface
          ref={surface}
          game={game}
          drawingId={task.drawingId}
          rules={task.rules}
          userId={view.me.userId}
          userName={(view.members.find((member) => member.userId === view.me.userId) || {}).name || ''}
          label={task.prompt ? `${task.label}: ${task.prompt}` : task.label}
          live={Boolean(task.live)}
          shared={Boolean(task.shared)}
          onionId={task.onion || ''}
          disabled={Boolean(task.submitted)}
        />
      </div>
      {timeline.length ? (
        <div className="party-timeline" aria-label="Flipbook so far">
          {timeline.map((id, k) => <div key={k} className="party-timeline-frame">{id ? <PartyDrawingView store={game.store} drawingId={id} width={64} /> : <span className="party-missing tiny">–</span>}</div>)}
          <div className="party-timeline-frame active">✏️</div>
        </div>
      ) : null}
      {task.partners && task.partners.length ? <div className="party-partners">Partner{task.partners.length > 1 ? 's' : ''}: {task.partners.map((p) => `${nameOf(view.game.players, p.userId)} (${p.part.toLowerCase()})`).join(', ')}. You can't see their part yet!</div> : null}
    </div>
  );
}

function Countdown({ phase, serverNow, prompt }) {
  const now = useServerClock(serverNow);
  const left = Math.max(0, Math.ceil(((phase.endsAt || now) - now) / 1000));
  return (
    <div className="party-countdown" data-testid="countdown">
      <div key={left} className="party-countdown-number">{left || 'GO!'}</div>
      {prompt ? <div className="party-countdown-prompt">{prompt}</div> : <div className="party-countdown-prompt">Get ready…</div>}
    </div>
  );
}

function Progress({ progress }) {
  if (!progress || !progress.total) return null;
  return (
    <div className="party-progress" aria-label={`${progress.done} of ${progress.total} done`}>
      <div className="party-progress-bar"><span style={{ width: `${Math.min(100, (progress.done / progress.total) * 100)}%` }} /></div>
      <span>{progress.done}/{progress.total} done</span>
    </div>
  );
}

function SpectatorNote({ view }) {
  if (view.me.role === 'player' && !view.members.find((m) => m.userId === view.me.userId && m.waiting)) return null;
  const waiting = view.members.find((m) => m.userId === view.me.userId && m.waiting);
  return <div className="party-spectator-note">{waiting ? 'You will join at the start of the next round.' : 'You are watching this game.'}</div>;
}

// Picks what the main area shows for the current mode and phase.
function MainArea({ game, view }) {
  const g = view.game;
  const mode = view.mode.id;
  const phase = view.phase || {};
  const task = g.task;
  const players = g.players;
  const emojis = view.reactions || [];
  const react = (target, emoji) => game.act('react', { target, emoji });
  const vote = (entryId) => game.act('vote', { entryId });

  // Phases without a personal task.
  if (phase.key === 'countdown') return <Countdown phase={phase} serverNow={game.serverNow} prompt={g.prompt} />;
  if (phase.key === 'memorize' && g.reference) {
    return (
      <div className="party-memorize" data-testid="memorize">
        <div className="party-task-label">Memorise this picture!</div>
        <PartyDrawingView items={g.reference.items} fluid label="Reference picture to memorise" />
      </div>
    );
  }
  if (phase.key === 'intro' && g.challenge) {
    return (
      <div className="party-challenge-card" data-testid="challenge-intro">
        <div className="party-challenge-kicker">Round {phase.round}: new challenge</div>
        <div className="party-challenge-name">{g.challenge.name}</div>
        <div className="party-challenge-blurb">{g.challenge.blurb}</div>
      </div>
    );
  }
  if (phase.key === 'reveal' && mode === 'sketch-relay' && g.reveal) return <RelayReveal reveal={g.reveal} store={game.store} players={players} emojis={emojis} onReact={react} />;
  if (phase.key === 'playback' && g.playback) {
    const book = g.playback.books[g.playback.current];
    return book ? <FlipbookPlayer key={book.index} book={book} store={game.store} players={players} defaultFps={g.fps} emojis={emojis} onReact={react} /> : null;
  }
  if (phase.key === 'reveal' && mode === 'bluff-brush') return <BluffReveal reveal={g.reveal} store={game.store} players={players} />;
  if (phase.key === 'reveal' && (mode === 'quick-draw' || mode === 'art-sabotage')) return <TurnReveal reveal={g.reveal} store={game.store} players={players} />;
  if (phase.key === 'reveal' && mode === 'team-canvas') return <TeamReveal reveal={g.reveal} teams={g.teams} store={game.store} players={players} />;
  if (phase.key === 'results' && mode === 'half-half') return <ComboResults results={g.results} store={game.store} players={players} />;
  if (phase.key === 'results' && mode === 'chaos-canvas') return <ChallengeResults results={g.results} store={game.store} players={players} />;
  if (phase.key === 'results' && g.results) return <RoundResults results={g.results} store={game.store} players={players} showSimilarity={Boolean(g.results.reference)} />;

  if (!task) {
    return (
      <div className="party-task wait">
        <Progress progress={g.progress} />
        <SpectatorNote view={view} />
      </div>
    );
  }
  if (task.kind === 'write' || task.kind === 'describe') {
    const action = WRITE_ACTION[mode] || 'submit';
    const drawingView = task.drawingId ? <PartyDrawingView store={game.store} drawingId={task.drawingId} fluid label="Drawing to describe" testId="describe-drawing" /> : (g.current && g.current.drawingId ? <PartyDrawingView store={game.store} drawingId={g.current.drawingId} fluid label="Drawing" /> : null);
    return (
      <>
        <WriteTask key={`${phase.seq}`} task={task} drawingView={drawingView} onSubmit={(text) => game.act(action, { text })} />
        <Progress progress={g.progress} />
      </>
    );
  }
  if (task.kind === 'draw') {
    let extra = null;
    if (g.twisted) extra = <div className="party-twist" role="alert">🌀 Plot twist! Now draw: <strong>{task.prompt}</strong></div>;
    if (g.challenge && phase.key !== 'start') extra = <>{extra}<div className="party-tag">{g.challenge.name}: {g.challenge.blurb}</div></>;
    return (
      <>
        <DrawTask key={task.drawingId} game={game} view={view} task={task} extra={extra} />
        <Progress progress={g.progress} />
      </>
    );
  }
  if (task.kind === 'choose') {
    return (
      <div className="party-task choose" data-testid="task-choose">
        <div className="party-task-label">Pick a word to draw</div>
        <div className="party-choices">
          {task.choices.map((choice, index) => (
            <button key={choice.text} type="button" className="party-choice" onClick={() => game.act('choose', { index })} data-testid={`choice-${index}`}>
              <strong>{choice.text}</strong>
              <span className={`party-difficulty ${choice.difficulty}`}>{choice.difficulty}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (task.kind === 'guess') {
    const board = g.board;
    const hint = (g.turn && g.turn.hint) || (board && board.hint) || task.hint || '';
    return (
      <div className="party-task guess" data-testid="task-guess">
        <div className="party-guess-head">
          {g.turn ? <span className="party-task-label">{nameOf(players, g.turn.drawerId)} is drawing{g.turn.category ? ` · ${g.turn.category}` : ''}</span> : null}
          {g.drawerId ? <span className="party-task-label">{nameOf(players, g.drawerId)} is drawing</span> : null}
          {task.guessed ? <div className="party-solved">You got it: <strong>{task.word}</strong> {task.points ? `(+${task.points})` : ''}</div> : <HintLetters hint={hint} />}
        </div>
        {g.turn && g.turn.restrictions && g.turn.restrictions.length ? <div className="party-restriction-tags">{g.turn.restrictions.map((entry) => <span key={entry.id} className="party-tag warn">{entry.label}</span>)}</div> : null}
        <PartyDrawingView store={game.store} drawingId={task.drawingId} fluid label="Live drawing" testId="live-drawing" />
        {board && board.feed ? (
          <div className="party-team-feed" aria-live="polite">
            {board.feed.map((entry, k) => <div key={k} className={`party-feed-entry ${entry.kind}`}><strong>{nameOf(players, entry.userId)}</strong> {entry.kind === 'correct' ? 'solved it!' : entry.text}</div>)}
          </div>
        ) : null}
        <GuessBox disabled={task.guessed} maxLength={task.maxLength} onGuess={(text) => game.act('guess', { text }, { quietErrors: false })} />
      </div>
    );
  }
  if (task.kind === 'watch') {
    const ids = task.drawingIds || (task.drawingId ? [task.drawingId] : []);
    if (g.vote) {
      return <VoteGallery vote={g.vote} store={game.store} onVote={vote} disabled layersFor={g.groups ? (entry) => (g.groups.find((group) => group.id === entry.id) || {}).layers : null} />;
    }
    return (
      <div className="party-task watch">
        <SpectatorNote view={view} />
        <div className="party-gallery-grid">{ids.map((id) => <PartyDrawingView key={id} store={game.store} drawingId={id} fluid />)}</div>
      </div>
    );
  }
  if (task.kind === 'wait') {
    return (
      <div className="party-task wait" data-testid="task-wait">
        <div className="party-wait-label">{task.label}</div>
        {g.current && g.current.drawingId ? <PartyDrawingView store={game.store} drawingId={g.current.drawingId} fluid /> : null}
        <Progress progress={g.progress} />
      </div>
    );
  }
  if (task.kind === 'vote') {
    const layersFor = g.groups ? (entry) => (g.groups.find((group) => group.id === entry.id) || {}).layers : null;
    return (
      <div className="party-task vote" data-testid="task-vote">
        <div className="party-task-label">{mode === 'bluff-brush' ? 'Which title is the real one?' : 'Vote for your favourite'}{g.prompt ? ` · “${g.prompt}”` : ''}</div>
        {mode === 'bluff-brush' && g.current ? <PartyDrawingView store={game.store} drawingId={g.current.drawingId} fluid label="The drawing" /> : null}
        {g.reference ? <figure className="party-reference inline"><PartyDrawingView items={g.reference.items} width={180} /><figcaption>Original</figcaption></figure> : null}
        <VoteGallery vote={mode === 'bluff-brush' ? { ...g.vote, entries: g.vote.entries.map((entry) => ({ ...entry, drawingId: '' })) } : g.vote} store={game.store} onVote={vote} layersFor={layersFor} labelFor={(entry) => entry.label} />
        <Progress progress={g.progress} />
      </div>
    );
  }
  return null;
}

function statusFor(view) {
  const g = view.game;
  if (!g) return null;
  return (userId) => {
    if (g.turn && g.turn.drawerId === userId && view.phase && view.phase.key === 'draw') return 'drawing';
    if (g.turn && (g.turn.guessedUserIds || []).includes(userId)) return 'guessed';
    if (g.board && g.board.artists && g.board.artists.some((entry) => entry.artistId === userId)) return 'drawing';
    return '';
  };
}

export default function PartyStage({ game, view, onLeave }) {
  const isHost = view.me.isHost;
  const phase = view.phase || {};
  const highlight = useMemo(() => statusFor(view), [view]);
  const [feedOpen, setFeedOpen] = useState(false);
  const showFeed = ['quick-draw', 'art-sabotage', 'chaos-canvas'].includes(view.mode.id) || (view.feed || []).length > 0;
  return (
    <div className="party-stage" style={{ '--party-accent': view.mode.accent }} data-testid="party-stage" data-phase={phase.key || ''} data-mode={view.mode.id}>
      <header className="party-stage-head">
        <div className="party-stage-title">
          <span className="party-mode-name">{view.mode.name}</span>
          <span key={phase.seq} className="party-phase-badge">{phase.label || phase.key}</span>
          {phase.totalRounds ? <span className="party-round">Round {Math.max(1, phase.round)} / {phase.totalRounds}</span> : null}
        </div>
        <div className="party-stage-actions">
          <PhaseTimer phase={phase} serverNow={game.serverNow} />
          {isHost && (phase.hostAdvance || phase.key) ? <button type="button" className="party-btn small ghost" onClick={() => game.act('skip')} title="Move on now">{phase.hostAdvance ? 'Next ▸' : 'Skip ▸'}</button> : null}
          {isHost ? <button type="button" className="party-btn small ghost danger" onClick={() => game.act('end')}>End game</button> : null}
          <button type="button" className="party-btn small ghost" onClick={onLeave}>Leave</button>
        </div>
      </header>
      <div className="party-stage-body">
        <aside className="party-stage-side">
          <PlayerStrip view={view} game={view.game} highlight={highlight} />
          {view.game.teams ? (
            <div className="party-team-scores">{view.game.teams.map((team) => <div key={team.id} className={`party-team-score${team.id === view.game.myTeam ? ' mine' : ''}`} style={{ borderColor: team.color }}><span style={{ color: team.color }}>{team.name}</span><strong>{team.score}</strong></div>)}</div>
          ) : null}
        </aside>
        <main className="party-stage-main">
          <MainArea game={game} view={view} />
        </main>
        {showFeed ? (
          <aside className={`party-stage-feed${feedOpen ? ' open' : ''}`}>
            <button type="button" className="party-feed-toggle" onClick={() => setFeedOpen((value) => !value)} aria-expanded={feedOpen}>Feed {feedOpen ? '▾' : '▸'}</button>
            <Feed view={view} />
          </aside>
        ) : null}
      </div>
    </div>
  );
}
